/**
 * DateFormatSelect — a compact Select for the FOUR declared date formats
 * (`iso` year-first / `mdy` US Month-Day / `dmy` Day-Month / `month_name`),
 * used where a column is already known to be date-typed and only the format is
 * chosen: the mapping row's date-format chip. The exact sibling of
 * {@link PercentVariantSelect} — no type axis, just the declared-fact value.
 *
 * With a `dataProvenFormat` hint, the format the column's own values PROVE
 * (calendar-validity evidence from `detectDateColumnFormat`) is marked "matches
 * your data" — a visible recommendation, never an override. A US-default
 * prefill for an ambiguous column carries NO such marker (the caller labels it
 * as a suggestion instead — data silence is not data support). Pure: owns no
 * state, calls `onChange(format)`.
 */
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { DECLARED_DATE_FORMATS, type DeclaredDateFormat } from '@repo/shared/common';
import { Select } from '~/components/ui';

export interface DateFormatSelectProps {
  /** The current declared format. */
  readonly value: DeclaredDateFormat;
  /** Called with the next format when it changes. */
  readonly onChange: (format: DeclaredDateFormat) => void;
  readonly disabled?: boolean;
  /** Mantine input size (default `sm`). */
  readonly size?: string;
  /** id/aria base for the select, e.g. `idBase="column-date-Snapshot Date"`. */
  readonly idBase?: string;
  /**
   * The format the column's own values PROVE (the detection's unambiguous
   * suggestion), or null when the data cannot settle one. Marks that option
   * "matches your data"; every explicit pick is honored regardless.
   */
  readonly dataProvenFormat?: DeclaredDateFormat | null;
}

export function DateFormatSelect({
  value,
  onChange,
  disabled = false,
  size = 'sm',
  idBase,
  dataProvenFormat = null,
}: DateFormatSelectProps): ReactNode {
  const { t } = useTranslation();

  const options = DECLARED_DATE_FORMATS.map((format) => ({
    value: format,
    label:
      format === dataProvenFormat
        ? t('dateFormats.matchesData', { label: t(`dateFormats.${format}`) })
        : t(`dateFormats.${format}`),
  }));

  return (
    <Select
      aria-label={t('dateFormats.selectLabel')}
      data={options}
      value={value}
      onChange={(next) => {
        // Mantine narrows `next` to the typed-`data` value union (DeclaredDateFormat).
        if (next === null || next === value) return;
        onChange(next);
      }}
      allowDeselect={false}
      disabled={disabled}
      size={size}
      comboboxProps={{ withinPortal: true }}
      {...(idBase !== undefined ? { id: `${idBase}-date-format` } : {})}
    />
  );
}
